import { useEffect, useState } from "react";
import Image from "next/legacy/image";

import { buildCloudflareImageUrl, imageLoader } from "utils";
import { useUserData } from "lib/store/user";
import * as reportUtils from "content-ui/intelligence/components/reportDownloadUtils";
import type { Report } from "content-ui/intelligence/types";
import { CheckboxWithText } from "./checkboxWithText/checkboxWithText";

const FeaturedReport = ({
  featuredReport,
  featuredReportSubtitle,
  checked,
  setCheck,
  openModal,
}: {
  featuredReport: Report;
  featuredReportSubtitle: string;
  checked: boolean;
  setCheck: Function;
  openModal: Function;
}) => {
  const { hasMissingInfo } = useUserData();

  const processedCheckboxText = (checkBoxText) => {
    return checkBoxText.replaceAll(/<p>|<\/p>/gi, "");
  };

  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const { readerSubscriptionLevel, isAuthorisedUser } = useUserData();
  const renderOpenDownload = (report: Report, isConsentRequired: boolean) => {
    const reportLink = report.pdf || report.url;
    return (
      <button
        className="mt-xl w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white disabled:bg-gray-500 md:mt-2xl md:w-auto md:text-xs"
        disabled={isConsentRequired && !checked}
        onClick={() => {
          reportUtils.downloadReportClick(
            reportLink,
            featuredReport.title,
            false,
            report.slug // Could not get an ID from WP ACF, so using the slug
          );
        }}
      >
        Download report
      </button>
    );
  };

  const baseSponsorImage = buildCloudflareImageUrl({
    src: featuredReport.sponsorImage,
    dpr: 1,
    fit: "scale-down",
    height: 50,
    format: "auto",
    quality: 75,
  });

  const sponsorImage2x = buildCloudflareImageUrl({
    src: baseSponsorImage,
    dpr: 2,
  });

  const renderRegisteredDownload = (report: Report) => {
    const onClickDownload = () => {
      if (hasMissingInfo) {
        completeProfileRedirect();
      } else {
        reportUtils.downloadReportClick(
          report.pdf,
          report.title,
          checked,
          report.slug // Could not get an ID from WP ACF, so using the slug
        );
      }
    };

    const completeProfileRedirect = () => {
      localStorage.setItem("report_redirect", report.title);
      // This is made on purpose - we need to force a page reload to get rendered components from zephr proxy
      window.location.href = "/members/complete-profile?redirectTarget=report";
    };

    const onClickRegister = () => {
      // This is made on purpose - we need to force a page reload to get rendered components from zephr proxy
      window.location.href = `/members/login?report=${encodeURIComponent(report.title)}`;
    };

    const onClickDownloadData = () => {
      window.open(report.reportDataFile, "_blank");
    };

    return (
      <>
        {isHydrated && (
          <>
            {/*
        TODO: this part of the component should be split out
        as (featuredReport & intelligence/card.tsx share this behaviour) once we re-write
      */}
            {/* Logged In */}
            {isAuthorisedUser && (
              <span className="flex flex-row space-x-4">
                {reportUtils.canAccessContent(
                  report,
                  readerSubscriptionLevel!
                ) ? (
                  <>
                    <button
                      disabled={isConsentRequired && !checked}
                      className="mt-lg inline-block w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white disabled:bg-gray-500 md:mt-xl md:w-auto md:text-xs"
                      onClick={onClickDownload}
                    >
                      Download report
                    </button>
                    {report.reportDataFile && (
                      <button
                        disabled={isConsentRequired && !checked}
                        className="mt-lg inline-block w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white disabled:bg-gray-500 md:mt-xl md:w-auto md:text-xs"
                        onClick={onClickDownloadData}
                      >
                        Download data
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    className="mt-lg inline-block w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white md:mt-xl md:w-auto md:text-xs"
                    onClick={() => {
                      if (report.membershipLevel === "Pro") {
                        openModal(true);
                      } else {
                        window.location.assign("/subscribe");
                      }
                    }}
                  >
                    {readerSubscriptionLevel !== "pro" &&
                    report.membershipLevel === "Pro"
                      ? "Join Pro to download"
                      : "Join to download"}
                  </button>
                )}
              </span>
            )}

            {/* Logged Out */}
            {!isAuthorisedUser && (
              <span>
                <button
                  className="mt-xl w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white md:mt-2xl md:w-auto md:text-xs"
                  onClick={() => {
                    report.membershipLevel === "Pro"
                      ? openModal(true)
                      : onClickRegister();
                  }}
                >
                  {readerSubscriptionLevel !== "pro" &&
                  report.membershipLevel === "Pro"
                    ? "Join Pro to download"
                    : "Join to download"}
                </button>
              </span>
            )}
          </>
        )}
      </>
    );
  };

  // This is an urgent request from the client. The logic for amending the text for all
  // reports is being cooked up by product, so this commit can be reverted once that's done.
  const sponsorTextOverride =
    featuredReport.title === "Spacetech" ? "Sponsored by" : null;

  const isConsentRequired =
    featuredReport.marketingConsentRequired === "Mandatory opt in";

  return (
    <div className="flex flex-col items-center justify-between text-center md:flex-row md:items-start md:text-left lg:mx-auto lg:w-4/5">
      <div>
        <h3 className="font-bold text-xl leading-tight md:text-2xl">
          {featuredReportSubtitle}
        </h3>
        <h2 className="mt-sm font-bold text-xl leading-none text-cerise md:text-3xl lg:leading-tight">
          {featuredReport.title}
        </h2>
        <p className="mt-md font-bold text-sm leading-snug text-gray-600 md:text-base lg:mr-2">
          {featuredReport.description}
        </p>

        {/* Desktop checkbox input */}
        {featuredReport.marketingConsentRequired === "Mandatory opt in" ||
        featuredReport.marketingConsentRequired === "Optional opt in" ? (
          <div className="my-2 hidden text-center md:flex md:text-left">
            {featuredReport.sponsorCheckboxText ? (
              <div className="mt-2 md:max-w-[90%] lg:max-w-[80%]">
                <CheckboxWithText
                  id={`reports_third_party-${featuredReport.title}`}
                  testId={"reports-consent"}
                  defaultChecked={checked}
                  onChange={(e) => setCheck(e.target.checked)}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: processedCheckboxText(
                        featuredReport.sponsorCheckboxText
                      ),
                    }}
                  />
                </CheckboxWithText>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="hidden md:inline-block">
          {featuredReport.membershipLevel !== "Free"
            ? renderRegisteredDownload(featuredReport)
            : renderOpenDownload(featuredReport, isConsentRequired)}
        </div>
      </div>

      <div className="mt-xl flex-shrink-0 md:ml-xl md:mt-0">
        <Image
          loader={imageLoader}
          width={230}
          height={330}
          alt="Sifted report shown in a magazine cover like fashion"
          src={featuredReport.image}
        />
        {featuredReport.sponsorImage ? (
          <div className="mt-sm hidden flex-col items-center justify-center md:flex">
            <p className="font-bold text-sm text-gray-600">
              {sponsorTextOverride
                ? sponsorTextOverride
                : featuredReport.membershipLevel === "Pro"
                  ? "Read exclusively with"
                  : "In partnership with"}
            </p>
            <img
              src={baseSponsorImage}
              srcSet={`${baseSponsorImage} 1x, ${sponsorImage2x} 2x`}
              // TODO: need sponsor name for alt tag
              alt="Sponsor logo"
              className="mt-sm max-w-[230px]"
              loading="lazy"
            />
          </div>
        ) : null}
      </div>

      {/* Mobile only: Show sponsor logo in the appropriate place */}
      {featuredReport.sponsorImage && (
        <div className="flex flex-col items-center justify-center text-center md:hidden">
          <p className="mt-xl font-bold text-xs text-gray-600">
            {featuredReport.membershipLevel === "Pro"
              ? "Read exclusively with"
              : "In partnership with"}
          </p>

          <img
            src={baseSponsorImage}
            srcSet={`${baseSponsorImage} 1x, ${sponsorImage2x} 2x`}
            // TODO: need sponsor name for alt tag
            alt="Sponsor logo"
            className="mt-sm max-w-[230px]"
            loading="lazy"
          />
        </div>
      )}

      {/* Mobile input */}
      {featuredReport.marketingConsentRequired === "Mandatory opt in" ||
      featuredReport.marketingConsentRequired === "Optional opt in" ? (
        <div className="my-2 text-center md:visible md:hidden lg:text-left">
          {featuredReport.sponsorCheckboxText ? (
            <div className="mt-2 md:max-w-[90%] lg:max-w-[80%]">
              <CheckboxWithText
                id={`reports_third_party-${featuredReport.title}-mobile`}
                testId={"reports-consent-mobile"}
                defaultChecked={checked}
                onChange={(e) => setCheck(e.target.checked)}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: processedCheckboxText(
                      featuredReport.sponsorCheckboxText
                    ),
                  }}
                />
              </CheckboxWithText>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="md:hidden">
        {featuredReport.membershipLevel !== "Free"
          ? renderRegisteredDownload(featuredReport)
          : renderOpenDownload(featuredReport, isConsentRequired)}
      </div>
    </div>
  );
};

export default FeaturedReport;
